import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { QUAL_LABELS, ENV_OPTIONS } from "@/lib/constants";
import { FindDecisionMaker } from "@/components/FindDecisionMaker";
import { Wand2 } from "lucide-react";

const EMPTY = {
  company_name: "", contact_name: "", title: "", email: "", phone: "", website: "",
  cage_code: "", users_in_scope: 0, lead_source: "Manual Entry", deal_stage: "New Quiz Lead",
  additional_contacts: [],
  qualification: {
    dod_contracts: "", asked_about_requirements: "", handles_cui: "", ssp_poam: "",
    environment: "", self_assessment: "", biggest_concern: "", timeline: "",
    who_owns_readiness: "", decision_maker_access: "",
  },
};

function QualSelect({ field, value, onChange }) {
  const spec = QUAL_LABELS[field];
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{spec.label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={`qual-${field}`}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent>
          {Object.entries(spec.options).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export function LeadFormDialog({ open, onOpenChange, config, onSaved, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showDM, setShowDM] = useState(false);
  const [enrichmentNote, setEnrichmentNote] = useState(null);

  useEffect(() => {
    if (initial) {
      setForm({ ...EMPTY, ...initial, qualification: { ...EMPTY.qualification, ...(initial.qualification || {}) } });
    } else {
      setForm(EMPTY);
    }
    setEnrichmentNote(null);
  }, [initial, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setQ = (k, v) => setForm((f) => ({ ...f, qualification: { ...f.qualification, [k]: v } }));

  const applyContact = (c, note) => {
    setForm((f) => ({
      ...f,
      contact_name: `${c.first_name} ${c.last_name}`.trim(),
      title: c.title || f.title,
      email: c.email || f.email,
      phone: c.phone || f.phone,
    }));
    setEnrichmentNote(note);
  };

  const saveAdditionalContact = (c, note) => {
    setForm((f) => ({ ...f, additional_contacts: [...(f.additional_contacts || []), { ...c, added_at: new Date().toISOString() }] }));
    setEnrichmentNote(note);
  };

  const submit = async () => {
    if (!form.company_name.trim()) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        company_name: form.company_name, contact_name: form.contact_name, title: form.title,
        email: form.email, phone: form.phone, website: form.website, cage_code: form.cage_code,
        users_in_scope: parseInt(form.users_in_scope) || 0, lead_source: form.lead_source,
        deal_stage: form.deal_stage, qualification: form.qualification,
        additional_contacts: form.additional_contacts || [],
        enrichment_note: enrichmentNote || undefined,
      };
      if (isEdit) {
        await api.put(`/leads/${initial.id}`, payload);
        toast.success("Lead updated and re-scored");
      } else {
        await api.post("/leads", payload);
        toast.success("Lead created and scored");
        setForm(EMPTY);
      }
      onOpenChange(false);
      onSaved?.();
    } catch {
      toast.error("Failed to save lead");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between pr-8">
            <DialogTitle className="font-display text-xl text-navy">{isEdit ? "Edit Lead" : "New Lead"}</DialogTitle>
            <Button
              type="button"
              size="sm"
              data-testid="find-decision-maker-button"
              onClick={() => setShowDM(true)}
              disabled={!form.company_name && !form.website}
              className="bg-navy text-white hover:bg-navy-700 mr-2"
            >
              <Wand2 className="h-4 w-4 mr-2 text-gold" aria-hidden="true" /> Find Decision Maker
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <div>
            <p className="overline text-gold-dark mb-3">Company Information</p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Company name *" testid="field-company_name" value={form.company_name} onChange={(v) => set("company_name", v)} />
              <Field label="Contact name" testid="field-contact_name" value={form.contact_name} onChange={(v) => set("contact_name", v)} />
              <Field label="Title" value={form.title} onChange={(v) => set("title", v)} />
              <Field label="Email" value={form.email} onChange={(v) => set("email", v)} />
              <Field label="Phone" value={form.phone} onChange={(v) => set("phone", v)} />
              <Field label="Website" value={form.website} onChange={(v) => set("website", v)} />
              <Field label="CAGE code" value={form.cage_code} onChange={(v) => set("cage_code", v)} />
              <Field label="Users in CMMC scope" type="number" value={form.users_in_scope} onChange={(v) => set("users_in_scope", v)} />
              <div className="space-y-1.5">
                <Label className="text-xs">Lead source</Label>
                <Select value={form.lead_source} onValueChange={(v) => set("lead_source", v)}>
                  <SelectTrigger data-testid="field-lead_source"><SelectValue /></SelectTrigger>
                  <SelectContent>{config.lead_sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Deal stage</Label>
                <Select value={form.deal_stage} onValueChange={(v) => set("deal_stage", v)}>
                  <SelectTrigger data-testid="field-deal_stage"><SelectValue /></SelectTrigger>
                  <SelectContent>{config.deal_stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div>
            <p className="overline text-gold-dark mb-3">Qualification Answers</p>
            <div className="grid grid-cols-2 gap-4">
              <QualSelect field="dod_contracts" value={form.qualification.dod_contracts} onChange={(v) => setQ("dod_contracts", v)} />
              <QualSelect field="asked_about_requirements" value={form.qualification.asked_about_requirements} onChange={(v) => setQ("asked_about_requirements", v)} />
              <QualSelect field="handles_cui" value={form.qualification.handles_cui} onChange={(v) => setQ("handles_cui", v)} />
              <QualSelect field="ssp_poam" value={form.qualification.ssp_poam} onChange={(v) => setQ("ssp_poam", v)} />
              <div className="space-y-1.5">
                <Label className="text-xs">Environment (email/files/collab)</Label>
                <Select value={form.qualification.environment} onValueChange={(v) => setQ("environment", v)}>
                  <SelectTrigger data-testid="qual-environment"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{ENV_OPTIONS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <QualSelect field="self_assessment" value={form.qualification.self_assessment} onChange={(v) => setQ("self_assessment", v)} />
              <QualSelect field="timeline" value={form.qualification.timeline} onChange={(v) => setQ("timeline", v)} />
              <QualSelect field="decision_maker_access" value={form.qualification.decision_maker_access} onChange={(v) => setQ("decision_maker_access", v)} />
              <Field label="Who owns CMMC readiness" value={form.qualification.who_owns_readiness} onChange={(v) => setQ("who_owns_readiness", v)} />
              <div className="col-span-2 space-y-1.5">
                <Label className="text-xs">Biggest concern</Label>
                <Textarea data-testid="qual-biggest_concern" rows={2} value={form.qualification.biggest_concern} onChange={(e) => setQ("biggest_concern", e.target.value)} />
              </div>
            </div>
          </div>

          {(form.additional_contacts || []).length > 0 && (
            <div>
              <p className="overline text-gold-dark mb-3">Additional Contacts</p>
              <div className="space-y-2" data-testid="additional-contacts-list">
                {form.additional_contacts.map((c, i) => (
                  <div key={i} className="flex items-center justify-between rounded-md border border-border bg-slate-50 px-3 py-2 text-sm">
                    <div>
                      <span className="font-medium text-navy">{c.first_name} {c.last_name}</span>
                      <span className="text-muted-foreground"> · {c.title || "—"} · {c.email || "no email"}</span>
                    </div>
                    <span className="text-[11px] text-muted-foreground">{c.confidence} · {c.source}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="save-lead-button" onClick={submit} disabled={saving}
            className="bg-navy text-white hover:bg-navy-700">
            {saving ? "Scoring…" : isEdit ? "Save & Re-score" : "Create & Score Lead"}
          </Button>
        </DialogFooter>
      </DialogContent>

      <FindDecisionMaker
        open={showDM}
        onOpenChange={setShowDM}
        lead={form}
        hasExistingContact={!!(form.contact_name || form.email || form.phone)}
        onApply={applyContact}
        onSaveAdditional={saveAdditionalContact}
      />
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
